import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useHybridCart } from "@/contexts/HybridCartContext";
import { Loader2, CreditCard, Banknote, Truck } from "lucide-react";
import api from "@/services/api";
import { TestCardNotice } from "./TestCardNotice";

const createCardPaymentSchema = () =>
  z.object({
    cardName: z.string().min(2, "Nombre requerido"),
    cardNumber: z.string().regex(/^\d{16}$/, "16 dígitos"),
    expiryDate: z.string().regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "MM/YY"),
    cvc: z.string().regex(/^\d{3,4}$/, "3-4 dígitos"),
  });

type CardPaymentFormData = z.infer<ReturnType<typeof createCardPaymentSchema>>;

export const CardPaymentForm = (
  { onBack, onSuccess, orderId } /* : GuestCheckoutFormProps */
) => {
  const [isLoading, setIsLoading] = useState(false);

  const { cart, getCartItems } = useHybridCart();
  const { toast } = useToast();

  const cartItems = getCartItems();
  const subtotal = cart?.subtotal || 0;

  const cardPaymentSchema = z.object({
    cardName: z.string().min(2, "Nombre requerido"),
    cardNumber: z.string().regex(/^\d{16}$/, "16 dígitos"),
    expiryDate: z.string().regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "MM/YY"),
    cvc: z.string().regex(/^\d{3,4}$/, "3-4 dígitos"),
  });

  const form = useForm<CardPaymentFormData>({
    resolver: zodResolver(cardPaymentSchema),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form;

  // Auto-format expiry date as MM/YY
  const formatExpiryDate = (value: string) => {
    // Remove all non-digits
    const digitsOnly = value.replace(/\D/g, '');
    
    // Add slash after 2 digits
    if (digitsOnly.length >= 2) {
      return digitsOnly.slice(0, 2) + '/' + digitsOnly.slice(2, 4);
    }
    
    return digitsOnly;
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatExpiryDate(e.target.value);
    setValue('expiryDate', formatted);
  };

  const onSubmit = async (data: CardPaymentFormData) => {
    if (!orderId) {
      toast({
        title: "Error",
        description: "No se encontró el ID de la orden",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);

      // Enviar datos de tarjeta directamente al backend (PF maneja seguridad)
      const response = await api.post("/checkout/pay", {
        orderId,
        cardData: {
          cardName: data.cardName,
          cardNumber: data.cardNumber.replace(/\s/g, ""),
          expiryDate: data.expiryDate,
          cvc: data.cvc,
        }
      });

      onSuccess(response.data);
    } catch (error: unknown) {
      console.error("Payment error:", error);
      let errorMessage = "Error al procesar el pago";

      if (error && typeof error === "object" && "message" in error) {
        errorMessage = String(error.message);
      }

      toast({
        title: "Error en el pago",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) {
      console.log("orderId received:", orderId);
    }
  }, [orderId]);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Checkout Form */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-brown-dark">Checkout</h2>
            <Button variant="outline" onClick={onBack}>
              ← Volver
            </Button>
          </div>

          <TestCardNotice />
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Información de pago</CardTitle>
                {/* <CardDescription>
                  Te enviaremos la confirmación y seguimiento a tu email
                </CardDescription> */}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4 pt-4 mt-4 border-t border-gray-200">
                  <div className="grid gap-2">
                    <Label htmlFor="cardName">Nombre en la Tarjeta</Label>
                    <Input
                      id="cardName"
                      placeholder="Juan Pérez"
                      {...register("cardName")}
                      className={errors.cardName ? "border-red-500" : ""}
                    />
                    {errors.cardName && (
                      <p className="text-sm text-red-500">
                        {errors.cardName.message}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="cardNumber">Número de Tarjeta</Label>
                    <Input
                      id="cardNumber"
                      placeholder="0000 0000 0000 0000"
                      {...register("cardNumber")}
                      className={errors.cardNumber ? "border-red-500" : ""}
                    />
                    {errors.cardNumber && (
                      <p className="text-sm text-red-500">
                        {errors.cardNumber.message}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="expiryDate">Expiración (MM/YY)</Label>
                      <Input
                        id="expiryDate"
                        placeholder="MM/YY"
                        value={watch('expiryDate') || ''}
                        onChange={handleExpiryChange}
                        maxLength={5}
                        className={errors.expiryDate ? "border-red-500" : ""}
                      />
                      {errors.expiryDate && (
                        <p className="text-sm text-red-500">
                          {errors.expiryDate.message}
                        </p>
                      )}
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="cvc">CVC</Label>
                      <Input
                        id="cvc"
                        placeholder="123"
                        {...register("cvc")}
                        className={errors.cvc ? "border-red-500" : ""}
                      />
                      {errors.cvc && (
                        <p className="text-sm text-red-500">
                          {errors.cvc.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            {/* <Card>
              <CardHeader>
                <CardTitle>Notas adicionales (opcional)</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Instrucciones especiales para la entrega..."
                  {...register("notes")}
                  rows={3}
                />
              </CardContent>
            </Card> */}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary-dark text-brown-dark text-lg py-6"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
              Finalizar pedido • USD {(subtotal || 0).toFixed(2)}
            </Button>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="flex items-center justify-center gap-2 text-sm text-brown">
                🔒 Pago seguro y encriptado (SSL)
              </p>
              <div className="mt-3 flex items-center justify-center gap-4">
                <img src="/payments/visa.svg" alt="Visa" className="h-6" />
                <img
                  src="/payments/mastercard.svg"
                  alt="Mastercard"
                  className="h-6"
                />
                <img src="/payments/yappy.svg" alt="Yappy" className="h-6" />
              </div>
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="lg:sticky lg:top-6 h-fit">
          <Card>
            <CardHeader>
              <CardTitle>Resumen del pedido</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {cartItems.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex-shrink-0">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-sm text-brown">
                        Cantidad: {item.quantity}
                      </p>
                    </div>
                    <p className="font-medium">
                      USD {(item.subtotal || 0).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              <hr />
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>USD {(subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Envío</span>
                  <span className="text-sm text-brown">
                    Se le indicará por WhatsApp
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>USD {(subtotal || 0).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
